import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge, type BadgeProps } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import {
  costumeFlags,
  useCostumeProductionDetail,
  useCostumeStatusReport,
  type CostumeFulfillmentStatus,
  type CostumePaymentStatus,
  type CostumeProductionDetailRow,
  type CostumeStatusReport,
} from '@/data/costumes';
import { useProductions } from '@/data/productions';

// Costume Command Center (v1). Production selector → readiness header → per
// dancer assignments table. Built on the same layout primitives as the owner
// dashboard (StatCard / Section / SimpleTable) so it reads as one product.
//
// The /admin parent route already gates on the `admin` role server-side, so
// this child route inherits that guard (matches every other admin.* route).
export const Route = createFileRoute('/admin/costumes/')({
  component: CostumeCommandCenter,
});

type BadgeTone = NonNullable<BadgeProps['tone']>;

// ---------------------------------------------------------------------------
// Formatting + status → badge tone maps
// ---------------------------------------------------------------------------

function titleize(value: string | null | undefined): string {
  if (!value) return 'Unspecified';
  return value
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// Fulfillment status drives the row's primary signal: green once the costume is
// in the dancer's hands, warn while it is in flight, danger for the exception
// states the studio must act on.
const fulfillmentTone: Record<CostumeFulfillmentStatus, BadgeTone> = {
  unassigned: 'neutral',
  assigned: 'info',
  ordered: 'info',
  received: 'accent',
  distributed: 'success',
  missing: 'danger',
  damaged: 'danger',
  replacement_required: 'danger',
};

const paymentTone: Record<CostumePaymentStatus, BadgeTone> = {
  unpaid: 'warn',
  partial: 'info',
  paid: 'success',
  waived: 'neutral',
};

function fulfillmentBadgeTone(status: string): BadgeTone {
  return fulfillmentTone[status as CostumeFulfillmentStatus] ?? 'neutral';
}

function paymentBadgeTone(status: string): BadgeTone {
  return paymentTone[status as CostumePaymentStatus] ?? 'neutral';
}

// Confidence is a 0–1 score from the recommender. Bucket it so a glance tells
// the studio whether to trust the suggested size or double-check the dancer.
function confidenceTone(confidence: number): BadgeTone {
  if (confidence >= 0.75) return 'success';
  if (confidence >= 0.4) return 'warn';
  return 'danger';
}

function formatConfidence(confidence: number): string {
  return `${Math.round(confidence * 100)}%`;
}

function dancerName(row: CostumeProductionDetailRow): string {
  return `${row.dancer_first} ${row.dancer_last}`.trim() || 'Dancer';
}

// `total` may be 0 for a fresh production; guard the percentage math.
function percent(part: number, total: number): string {
  if (total <= 0) return '—';
  return `${Math.round((part / total) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Layout primitives (mirrors admin.dashboard.tsx)
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Readiness header
// ---------------------------------------------------------------------------

function ReadinessHeader({ report }: { report: CostumeStatusReport }) {
  const total = report.total;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <StatCard
        label="Measured"
        value={percent(report.measured, total)}
        hint={`${report.measured} of ${total} dancers`}
      />
      <StatCard
        label="Sized"
        value={percent(report.sized, total)}
        hint={`${report.sized} of ${total} sized`}
      />
      <StatCard
        label="Ordered"
        value={percent(report.ordered, total)}
        hint={`${report.ordered} of ${total} ordered`}
      />
      <StatCard
        label="Received"
        value={percent(report.received, total)}
        hint={`${report.received} of ${total} received`}
      />
      <StatCard
        label="Distributed"
        value={percent(report.distributed, total)}
        hint={`${report.distributed} of ${total} handed out`}
      />
      <StatCard
        label="Costume $ collected"
        value={formatCents(report.paid_cents)}
        hint={`of ${formatCents(report.cost_cents)} total cost`}
      />
      <StatCard
        label="Exceptions"
        value={report.exceptions}
        hint="Missing / damaged / replacement"
      />
      <StatCard label="Total costumes" value={total} hint="Assignments in production" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assignments table
// ---------------------------------------------------------------------------

function AssignmentsTable({ rows }: { rows: CostumeProductionDetailRow[] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="px-4 py-3 font-medium">Dancer</th>
            <th className="px-4 py-3 font-medium">Costume</th>
            <th className="px-4 py-3 font-medium">Vendor</th>
            <th className="px-4 py-3 font-medium">Size</th>
            <th className="px-4 py-3 font-medium">Recommended</th>
            <th className="px-4 py-3 font-medium">Fulfillment</th>
            <th className="px-4 py-3 font-medium">Payment</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const flags = costumeFlags(row.flags);
            return (
              <tr
                key={row.assignment_id}
                className="border-b border-[var(--color-border)] last:border-0"
              >
                <td className="px-4 py-3 align-top font-medium">{dancerName(row)}</td>
                <td className="px-4 py-3 align-top">
                  <div>{row.costume_name}</div>
                  {row.garment_type && (
                    <div className="text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                      {titleize(row.garment_type)}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 align-top text-[var(--color-ink-muted)]">
                  {row.vendor_name || '—'}
                </td>
                <td className="px-4 py-3 align-top tabular-nums">{row.assigned_size || '—'}</td>
                <td className="px-4 py-3 align-top">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="tabular-nums">{row.recommended_size || '—'}</span>
                    {row.recommended_size && (
                      <Badge
                        tone={confidenceTone(row.confidence)}
                        aria-label={`Recommendation confidence ${formatConfidence(row.confidence)}`}
                      >
                        {formatConfidence(row.confidence)}
                      </Badge>
                    )}
                    {flags.map((flag) => (
                      <Badge key={flag} tone="warn">
                        {titleize(flag)}
                      </Badge>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <Badge tone={fulfillmentBadgeTone(row.fulfillment_status)}>
                    {titleize(row.fulfillment_status)}
                  </Badge>
                </td>
                <td className="px-4 py-3 align-top">
                  <Badge tone={paymentBadgeTone(row.payment_status)}>
                    {titleize(row.payment_status)}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Command Center
// ---------------------------------------------------------------------------

function CostumeCommandCenter() {
  const productions = useProductions();
  const [productionId, setProductionId] = useState('');

  const statusReport = useCostumeStatusReport(productionId || undefined);
  const detail = useCostumeProductionDetail(productionId || undefined);

  const selectedProduction = useMemo(
    () => (productions.data ?? []).find((production) => production.id === productionId) ?? null,
    [productions.data, productionId],
  );

  const detailRows = detail.data ?? [];
  const hasSelection = Boolean(productionId);

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Recital operations
        </p>
        <h1 className="mt-2">Costume Command Center</h1>
      </header>

      {/* Production selector ------------------------------------------------ */}
      <section aria-labelledby="production-heading" className="surface p-5">
        <h2 id="production-heading" className="text-[var(--text-xl)]">
          Production
        </h2>
        <p className="mt-1 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
          Choose a production to see costume readiness and per-dancer assignments.
        </p>
        <label className="mt-4 grid max-w-xl gap-2">
          <span className="font-medium text-[var(--text-sm)]">Production</span>
          <select
            className="h-11 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
            value={productionId}
            onChange={(event) => setProductionId(event.target.value)}
          >
            <option value="">Choose a production</option>
            {(productions.data ?? []).map((production) => (
              <option key={production.id} value={production.id}>
                {production.name} · {production.season}
              </option>
            ))}
          </select>
        </label>
      </section>

      {!hasSelection ? (
        <Empty
          title="Select a production"
          description="Pick a production above to load its costume readiness rollup and assignment roster."
        />
      ) : (
        <>
          {/* Readiness header ---------------------------------------------- */}
          <Section
            id="readiness"
            title="Readiness"
            description={
              selectedProduction
                ? `Measured, sized, ordered, received, and distributed progress for ${selectedProduction.name}, plus costume payments collected and open exceptions.`
                : 'Measured, sized, ordered, received, and distributed progress, plus costume payments collected and open exceptions.'
            }
          >
            {statusReport.isLoading ? (
              <p aria-live="polite">Loading…</p>
            ) : statusReport.error ? (
              <Empty
                title="Readiness unavailable"
                description="The costume status report could not be loaded for this production."
              />
            ) : !statusReport.data || statusReport.data.total === 0 ? (
              <Empty
                title="No costumes yet"
                description="No costume assignments exist for this production. Assign costumes to a company, class, or dance to populate the readiness rollup."
              />
            ) : (
              <ReadinessHeader report={statusReport.data} />
            )}
          </Section>

          {/* Assignments table --------------------------------------------- */}
          <Section
            id="assignments"
            title="Assignments"
            description="Every dancer's costume with its assigned and recommended size, fulfillment state, and payment state."
          >
            {detail.isLoading ? (
              <p aria-live="polite">Loading…</p>
            ) : detail.error ? (
              <Empty
                title="Assignments unavailable"
                description="The costume assignment detail could not be loaded for this production."
              />
            ) : detailRows.length === 0 ? (
              <Empty
                title="No assignments"
                description="Dancers with an assigned costume will appear here once costumes are assigned."
              />
            ) : (
              <AssignmentsTable rows={detailRows} />
            )}
          </Section>

          {/*
            TODO(v1 follow-ups):
              - Measurement entry: surface useUpsertMeasurementProfile() in a
                per-dancer form so studios can capture measurements inline.
              - Assign costumes: a "Assign costume" action wired to
                useAssignCostumes() (company/class/dance scope) + a
                useRecommendSizePreview() preview before assigning.
              - Fulfillment kanban: drag assignments across fulfillment_status
                columns; recompute readiness on drop.
              - Per-row recommend: useRecommendSize() action to (re)compute a
                size for a single assignment.
          */}
        </>
      )}
    </div>
  );
}
